import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

export interface CoogoStudioHomeRenewalCopy {
  studio: string;
  showroom: string;
  account: string;
  firstTitle: string;
  firstSubtitle: string;
  firstPlaceholder: string;
  returningPlaceholder: string;
  returningPrefix: string;
  returningSuffix: string;
  sourceAction: string;
  /**
   * The attach chip when no .fig importer is reachable (appliance builds set
   * `VITE_OPENPENCIL_IMPORT_API_URL` empty on purpose). Advertising "Figma"
   * there sends the reader through file-pick -> upload -> failure to learn
   * what this label could have said up front.
   */
  sourceActionNoFigma: string;
  conversationHint: string;
  contextTitle: string;
  contextHint: string;
  botContextTitle: string;
  botContextDetail: string;
  botContextAction: string;
  projectChatContextTitle: string;
  projectChatContextDetail: string;
  projectChatContextAction: string;
  projectChatContextUnavailable: string;
  advancedStartHint: string;
  openProject: string;
  reviewProject: string;
  viewAllProjects: string;
  showWorkingProducts: string;
  showroomHint: string;
  carriedRequest: string;
  carriedRequestDetail: string;
  clarificationLabel: string;
  recentWork: string;
  needsAttention: string;
  startAnotherWay: string;
  noWorkYet: string;
  /**
   * The empty-workspace entry door (see `lib/starter-app-door.ts` for why it
   * exists). `noWorkYet` states the fact; these four give it somewhere to go.
   * `starterDoorProjectName` is a PROJECT name, not a label — it is what the
   * created project is called in the sidebar afterwards, and the creator can
   * rename it like any other.
   */
  starterDoorLead: string;
  starterDoorAction: string;
  starterDoorProjectName: string;
  starterDoorFailed: string;
  newProject: string;
  sources: string;
  starters: string;
  /**
   * Task 4 (as-is -> to-be conversion UX): the start-mode chip on the chat
   * home. This is CONVERSATION STATE ONLY (`hero-chat-input.tsx`'s local
   * `startMode`) -- the truth of "this project is a conversion" is the
   * presence of `settings.asIsSnapshot` on the project, never this choice.
   */
  /**
   * The accessible name of the chip GROUP itself. Distinct from
   * `startModeNew` on purpose: labelling the group with one of its own
   * options makes a screen reader announce "Start something new" as the
   * group that contains "Start something new" and "Convert an existing
   * screen".
   */
  startModeGroupLabel: string;
  startModeNew: string;
  startModeConversion: string;
  conversionAsk: string;
  /**
   * Task 5 (as-is -> to-be conversion UX): fires at SUBMIT time when
   * `startMode === 'conversion'` but the submitted text carries no original
   * a screen could be derived from (`deriveAsIsSnapshotFromPrompt` returns
   * null) -- so the creator learns, before waiting on generation, that this
   * run is proceeding as new rather than as the conversion they asked for.
   */
  conversionWithoutOriginal: string;
  projectStates: Record<"draft" | "active" | "published" | "archived", string>;
  proofStates: Record<"verified" | "review" | "blocked" | "none", string>;
  /**
   * S1 of the WonBanking scenarios doc: a pasted contract is answered with a
   * deterministic read-back — "this is what I read out of your document" —
   * before any generation. `{count}` placeholders are substituted verbatim.
   * `unfilledWarning` carries the doc's trust-deciding fact: response fields
   * the server never fills must be said BEFORE the screen is built.
   */
  contractReadback: {
    title: string;
    endpointCount: string;
    fieldCount: string;
    unfilledWarning: string;
    ambiguousNote: string;
    /**
     * Declarations whose fields the reader could not walk. Without this line
     * "{count} declared fields" is a confident summary of a document that was
     * only partly read, and a reader has no way to tell the difference.
     *
     * Says "declarations", not "types", because the list carries both: type
     * names (composed with `allOf`, referenced elsewhere) AND endpoint-scoped
     * labels like `getOrderList request` for a request/response block that
     * names neither its own fields nor a type this document defines. The
     * copy has to describe what is actually in the list — a line labelled
     * "declared types" over an entry that is not a type name is the same
     * confident-summary defect one level down.
     */
    unreadTypes: string;
    confirm: string;
    dismiss: string;
  };
  /**
   * S2 of the WonBanking scenarios doc: a pasted message-spec field table is
   * answered with a checkbox pass — which fields appear on screen, which
   * travel internally. The parse is deterministic; the decision is the
   * human's. `{count}` placeholders are substituted verbatim.
   */
  fieldSpecReadback: {
    title: string;
    fieldCount: string;
    hint: string;
    sections: { input: string; output: string; common: string; unknown: string };
    sectionAllLabel: string;
    requiredMark: string;
    /**
     * Rendered when the table SAID a field is optional. Without it, "the
     * table says optional" and "I could not read your required column" are
     * the same blank pixels, and only one of them is the user's problem.
     */
    optionalMark: string;
    /** Column headers that matched no known kind — nothing here reflects them. */
    unreadColumns: string;
    /** Rows dropped for an empty name cell (an Excel merged-cell paste). */
    unreadRows: string;
    /**
     * The required column WAS found, and these are the values in it that the
     * reader could not interpret (`예`/`아니오`, `*`, a footnote marker).
     *
     * Deliberately worded as "your table says X, and I could not read it" —
     * NOT as "no required column". Those are the two states the widening
     * collapsed: before it, such a column was named under `unreadColumns`;
     * after it, the column was read, the values were not, and the field
     * rendered unmarked exactly like a table that never had the column.
     */
    unreadRequiredValues: string;
    summary: string;
    confirm: string;
    dismiss: string;
  };
  /**
   * S3 of the WonBanking scenarios doc: a pasted legacy screen source is a
   * CONVERSION, not a new build. The card is a comparison view — what was
   * read out of the legacy screen, what conversion does with it — plus the
   * difference list, where each detected trait gets the human's ruling:
   * carry as-is or improve. Faithful conversion is the baseline; improvement
   * is the deliberate act. `{count}` placeholders are substituted verbatim.
   */
  legacyScreenReadback: {
    title: string;
    hint: string;
    readColumn: string;
    convertColumn: string;
    fieldsLine: string;
    tablesLine: string;
    actionsLine: string;
    scriptletsLine: string;
    inlineHandlersLine: string;
    convertFieldsLine: string;
    convertTablesLine: string;
    convertActionsLine: string;
    convertScriptletsLine: string;
    convertInlineHandlersLine: string;
    decisionsTitle: string;
    keepLabel: string;
    improveLabel: string;
    traits: {
      tableLayout: string;
      alertConfirm: string;
      popupWindow: string;
      deprecatedMarkup: string;
    };
    summary: string;
    dataWarning: string;
    confirm: string;
    dismiss: string;
  };
  /**
   * S4 of the WonBanking scenarios doc: a pasted screen design document is
   * answered with what it decides — areas, quoted labels carried verbatim,
   * flow lines — and with what it does not: where the data comes from. The
   * card states the gap and points at the S1/S2 lanes; proceeding
   * screen-only is an explicit decision. `{count}` substituted verbatim.
   */
  screenDesignReadback: {
    title: string;
    countsLine: string;
    areasTitle: string;
    labelsTitle: string;
    flowsTitle: string;
    /**
     * Headings the read recognized as document structure and could report
     * nowhere — not as an area, not as the title, not through the flow list.
     * Rendered with the headings' OWN WORDS, never as a count: "1 section was
     * not read" tells the human nothing they can check against their
     * document, and `countsLine` already implied the shrunken number.
     */
    unreadHeadings: string;
    /** Shown ONLY when the read found no data reference in the document. */
    dataGap: string;
    /**
     * Code-shaped tokens the read saw and could not confirm as data
     * references. Shown alongside EITHER data line — beside the gap notice it
     * is what the human is being asked to rule on, and beside the declared
     * list it is why that list must not be read as the document's complete set.
     */
    dataCandidates: string;
    /**
     * The other half of that read: the document DOES name its data source,
     * and these are the references, verbatim. `dataGap` claimed the opposite
     * unconditionally, and the claim was persisted as the human's decision.
     */
    dataDeclared: string;
    proceed: string;
    /** The proceed label when the document declared its data source. */
    proceedWithData: string;
    dismiss: string;
  };
  /**
   * S7 of the WonBanking scenarios doc: "make it like this one" extracts the
   * STANDARD from the reference — structure, terminology, patterns — and
   * never clones it. Which term is standard vocabulary is the human's call
   * (chips, anti-clone default: nothing carried until marked). Identifiers
   * are excluded by rule. `{count}` substituted verbatim.
   */
  referenceExtractionReadback: {
    title: string;
    intentLabel: string;
    hint: string;
    dictionaryHit: string;
    dictionaryMiss: string;
    structureLabel: string;
    structureLegacyLine: string;
    structureDesignLine: string;
    termsTitle: string;
    allLabel: string;
    identifiersNote: string;
    summary: string;
    confirm: string;
    dismiss: string;
  };
}

const COPY: Record<LocaleCode, CoogoStudioHomeRenewalCopy> = {
  en: {
    studio: "studio",
    showroom: "Showroom",
    account: "Account",
    firstTitle: "What would you like to make?",
    firstSubtitle:
      "Describe it in your own words or connect a file. The rest continues as one conversation.",
    firstPlaceholder:
      "Describe the product, workflow, document, or change you need",
    returningPlaceholder:
      "Tell coogo anything — make something new or change an existing product",
    returningPrefix: "Welcome back —",
    returningSuffix: "is ready to continue from where you left it.",
    sourceAction: "Files · Figma, Word, HWPX",
    sourceActionNoFigma: "Files · Word, HWPX",
    conversationHint:
      "Mention a product by name to continue in its context. coogo may ask up to three questions only when required.",
    contextTitle: "Choose the working context",
    contextHint:
      "Bots work independently. Project chat opens the app and its IDE context before the conversation starts.",
    botContextTitle: "Ask a Bot",
    botContextDetail: "Request anything without opening a project or the IDE.",
    botContextAction: "Open Bots",
    projectChatContextTitle: "Continue in project chat",
    projectChatContextDetail: "Open {project} with its pages, data, and IDE context.",
    projectChatContextAction: "Open project chat",
    projectChatContextUnavailable: "Create or open a project first to use project chat.",
    advancedStartHint:
      "Start from a new request or bring in source files here.",
    openProject: "Open and work",
    reviewProject: "Review",
    viewAllProjects: "All projects",
    showWorkingProducts: "Browse working products",
    showroomHint:
      "A working example is optional. Choosing one starts a preview candidate; it never publishes or deploys automatically.",
    carriedRequest: "Continuing from coogo",
    carriedRequestDetail:
      "Your earlier request is already in this conversation. Review it before anything is created.",
    clarificationLabel: "Questions needed to continue",
    recentWork: "Recent work",
    needsAttention: "Needs attention",
    startAnotherWay: "Choose how to start",
    noWorkYet: "No work yet",
    starterDoorLead:
      "Open a finished app first and see it running — then change it, or start your own.",
    starterDoorAction: "Open the newsletter membership app",
    starterDoorProjectName: "Newsletter membership",
    starterDoorFailed: "Could not open the starter app",
    newProject: "Create from a request",
    sources: "Start from files or Figma",
    starters: "Start from a template",
    startModeGroupLabel: "How would you like to start?",
    startModeNew: "Start something new",
    startModeConversion: "Convert an existing screen",
    conversionAsk: "Paste the original screen and I'll read it before we change anything.",
    conversionWithoutOriginal:
      "No original was provided, so this is being built as new — not a conversion.",
    projectStates: {
      draft: "draft",
      active: "active",
      published: "published",
      archived: "archived",
    },
    proofStates: {
      verified: "checked on the live site",
      review: "evidence needs review",
      blocked: "the next step is blocked",
      none: "not checked live yet",
    },
      contractReadback: {
      title: "Read from your document",
      endpointCount: "{count} endpoints",
      fieldCount: "{count} declared fields",
      unfilledWarning:
        "{count} response fields are declared but never filled by the server — they will always render empty:",
      ambiguousNote:
        "{count} points are left undecided by the document — they come back as questions before anything is built.",
      unreadTypes:
        "{count} declarations were not read — either a type whose fields are composed from, or defined by, something outside this document, or a request/response block naming neither its own fields nor a type defined here. The count above does not include them:",
      confirm: "That's right — continue",
      dismiss: "Not yet — let me edit",
    },
    fieldSpecReadback: {
      title: "Read from your field table",
      fieldCount: "{count} fields",
      hint: "Check only the fields that should appear on screen — a message spec is never dumped onto a page whole.",
      sections: {
        input: "Input (query conditions)",
        output: "Output (results)",
        common: "Common",
        unknown: "Unsectioned",
      },
      sectionAllLabel: "all",
      requiredMark: "required",
      optionalMark: "optional",
      unreadColumns:
        "{count} columns of your table were not read, so nothing on this card reflects them:",
      unreadRows:
        "{count} rows had an empty name cell and were not read — an Excel merged cell pastes this way. The count above is what was read, not the size of your table.",
      unreadRequiredValues:
        "Your table HAS a required column, and these are the values in it this reader could not interpret — the fields in this table carry no required mark because of that, not because your table left it blank:",
      summary: "{visible} on screen · {internal} internal",
      confirm: "Continue with this decision",
      dismiss: "Not yet — let me edit",
    },
    legacyScreenReadback: {
      title: "Read from your legacy screen",
      hint: "This is a conversion, not a new build — decide what carries over as-is and what improves.",
      readColumn: "Read from the legacy screen",
      convertColumn: "After conversion",
      fieldsLine: "{count} input fields",
      tablesLine: "{count} tables",
      actionsLine: "{count} buttons / actions",
      scriptletsLine: "{count} server scriptlets",
      inlineHandlersLine: "{count} inline event handlers",
      convertFieldsLine: "{count} input components",
      convertTablesLine: "data tables (columns kept)",
      convertActionsLine: "{count} events / ActionFlows",
      convertScriptletsLine: "do NOT travel — a data source is needed",
      convertInlineHandlersLine: "converted to event bindings automatically",
      decisionsTitle: "Differences — carry as-is, or improve?",
      keepLabel: "as-is",
      improveLabel: "improve",
      traits: {
        tableLayout: "Table-based layout",
        alertConfirm: "alert/confirm dialogs",
        popupWindow: "Popup windows (window.open)",
        deprecatedMarkup: "Deprecated markup (font/center/bgcolor)",
      },
      summary: "as-is {keep} · improve {improve}",
      dataWarning: "{count} pieces of server logic do not travel with the screen — say where the data comes from with a contract or a field table.",
      confirm: "Convert with this ruling",
      dismiss: "Not yet — let me edit",
    },
    screenDesignReadback: {
      title: "Read from your design document",
      countsLine: "{areas} areas · {labels} labels · {flows} flow lines",
      areasTitle: "Area composition",
      labelsTitle: "Labels — quoted copy is used verbatim",
      flowsTitle: "Screen flow",
      unreadHeadings:
        "Read as a section of your document, but not shown as a screen area — nothing under it reached this card:",
      dataCandidates:
        "These look like field codes, but nothing in the document says what they are bound to — they were NOT counted as a data source, and nothing will be bound to them:",
      dataGap:
        "The screen is here — the data is not. No field-to-transaction binding could be read from this document. Attach a contract or a message-spec field table and the data connects too.",
      dataDeclared:
        "The data source is in this document — these are the references read out of it, and they travel with the screen:",
      proceed: "Proceed screen-first",
      proceedWithData: "Continue with this reading",
      dismiss: "Not yet — let me edit",
    },
    referenceExtractionReadback: {
      title: "Extract the standard from your reference",
      intentLabel: "To build:",
      hint: "The reference is not cloned — only the standard is carried. Tap a term to mark it standard vocabulary; unmarked terms stay this screen's own.",
      dictionaryHit: 'Defined in the standard terminology index — a standard candidate',
      dictionaryMiss: 'Not yet defined in the terminology index — may be screen-specific',
      structureLabel: "Carry the structure pattern",
      structureLegacyLine: "{fields} inputs · {tables} tables · {actions} actions",
      structureDesignLine: "{areas} areas · {flows} flow lines",
      termsTitle: "Terms — standard, or this screen's own?",
      allLabel: "all standard",
      identifiersNote: "{count} field identifiers are not carried — the new screen's fields come from its own contract or field table.",
      summary: "standard {standard} · this screen's own {specific}",
      confirm: "Extract the standard and continue",
      dismiss: "Not yet — let me edit",
    },
  },
  ko: {
    studio: "studio",
    showroom: "Showroom",
    account: "계정",
    firstTitle: "무엇을 만들고 싶으세요?",
    firstSubtitle:
      "말로 설명해도, 가진 파일을 연결해도 됩니다 — 나머지는 하나의 대화로 이어집니다.",
    firstPlaceholder: "필요한 제품, 업무, 문서 또는 변경 사항을 설명하세요",
    returningPlaceholder:
      "무엇이든 말씀하세요 — 새로 만들어도, 지금 제품을 바꿔도 됩니다",
    returningPrefix: "돌아오셨네요 —",
    returningSuffix: "에서 하던 일을 그대로 이어갈 수 있어요.",
    sourceAction: "파일 · Figma, Word, HWPX",
    sourceActionNoFigma: "파일 · Word, HWPX",
    conversationHint:
      "제품 이름을 말하면 그 제품의 대화로 이어집니다. 꼭 필요한 경우에만 최대 3가지를 묻습니다.",
    contextTitle: "작업할 컨텍스트를 선택하세요",
    contextHint:
      "봇은 프로젝트 없이 독립적으로 동작합니다. 프로젝트 채팅은 앱과 편집기 컨텍스트를 연 뒤 대화를 시작합니다.",
    botContextTitle: "봇에게 요청",
    botContextDetail: "프로젝트나 편집기를 열지 않고 무엇이든 요청합니다.",
    botContextAction: "봇 열기",
    projectChatContextTitle: "프로젝트 채팅에서 계속",
    projectChatContextDetail: "{project}의 화면·데이터·편집기 컨텍스트를 열고 대화합니다.",
    projectChatContextAction: "프로젝트 채팅 열기",
    projectChatContextUnavailable: "프로젝트 채팅은 프로젝트를 만들거나 연 뒤 사용할 수 있습니다.",
    advancedStartHint:
      "새 요청으로 시작하거나 소스 파일을 가져올 수 있습니다.",
    openProject: "열어서 작업하기",
    reviewProject: "확인하기",
    viewAllProjects: "내 프로젝트",
    showWorkingProducts: "지금 켜져 있는 완성품 구경하기",
    showroomHint:
      "완성품 선택은 선택 사항입니다. 선택해도 프리뷰 후보만 만들며 자동 발행·배포하지 않습니다.",
    carriedRequest: "coogo에서 하던 대화",
    carriedRequestDetail:
      "앞에서 설명한 요청을 이 대화에 그대로 가져왔어요. 만들기 전에 먼저 확인할 수 있습니다.",
    clarificationLabel: "계속하려면 필요한 질문",
    recentWork: "최근 작업",
    needsAttention: "확인이 필요한 작업",
    startAnotherWay: "시작 방법",
    noWorkYet: "아직 작업이 없습니다",
    starterDoorLead:
      "완성된 앱을 먼저 열어 동작을 확인해 보세요. 그대로 고쳐 쓰거나, 새로 시작해도 됩니다.",
    starterDoorAction: "뉴스레터 멤버십 앱 열기",
    starterDoorProjectName: "뉴스레터 멤버십",
    starterDoorFailed: "스타터 앱을 열지 못했습니다",
    newProject: "새로 만들기",
    sources: "파일·Figma에서 시작",
    starters: "스타터로 시작",
    startModeGroupLabel: "어떻게 시작할까요?",
    startModeNew: "새로 만들기",
    startModeConversion: "기존 화면 전환",
    conversionAsk: "원본 화면을 붙여넣어 주세요. 바꾸기 전에 먼저 읽어 드립니다.",
    conversionWithoutOriginal: "원본이 없어 신규 구축으로 진행합니다 — 전환이 아닙니다.",
    projectStates: {
      draft: "초안",
      active: "작업 중",
      published: "발행됨",
      archived: "보관됨",
    },
    proofStates: {
      verified: "운영 환경에서 확인됨",
      review: "근거 확인 필요",
      blocked: "다음 단계 차단됨",
      none: "운영 환경 확인 전",
    },
      contractReadback: {
      title: "문서에서 읽어낸 것",
      endpointCount: "거래(엔드포인트) {count}건",
      fieldCount: "선언된 필드 {count}개",
      unfilledWarning:
        "선언만 되고 서버가 채우지 않는 응답 필드 {count}개 — 화면에 항상 빈칸으로 나옵니다:",
      ambiguousNote:
        "문서가 정하지 않은 지점 {count}곳 — 만들기 전에 질문으로 확인합니다.",
      unreadTypes:
        "읽지 못한 선언이 {count}개 있습니다 — 필드가 이 문서 밖에서 정의·조합된 타입이거나, 자기 필드도 이 문서에 정의된 타입도 가리키지 않는 요청/응답 블록입니다. 위 개수에는 포함되지 않았습니다:",
      confirm: "맞습니다 — 이대로 진행",
      dismiss: "아직입니다 — 수정할게요",
    },
    fieldSpecReadback: {
      title: "전문 필드 표에서 읽어낸 것",
      fieldCount: "필드 {count}개",
      hint: "화면에 보일 필드만 체크하세요 — 전문 필드 전부를 화면에 뿌리지 않습니다.",
      sections: {
        input: "입력부 (조회 조건)",
        output: "출력부 (결과)",
        common: "공통부",
        unknown: "구분 미지정",
      },
      sectionAllLabel: "전체",
      requiredMark: "필수",
      optionalMark: "선택",
      unreadColumns:
        "표의 열 {count}개는 읽지 못했습니다. 이 카드에는 반영되어 있지 않습니다:",
      unreadRows:
        "필드명 칸이 비어 있어 읽지 못한 행이 {count}개 있습니다 — 엑셀 병합 셀을 붙여넣으면 이렇게 됩니다. 위 개수는 읽어낸 필드 수이며 표의 크기가 아닙니다.",
      unreadRequiredValues:
        "표에 필수 열은 있는데, 그 안의 다음 값을 읽어내지 못했습니다 — 이 표의 필드에 필수 표시가 없는 것은 표가 비어 있어서가 아니라 이 때문입니다:",
      summary: "화면 노출 {visible}개 · 내부 전달 {internal}개",
      confirm: "이 결정으로 진행",
      dismiss: "아직입니다 — 수정할게요",
    },
    legacyScreenReadback: {
      title: "레거시 화면에서 읽어낸 것",
      hint: "새로 만들기가 아니라 전환입니다 — 그대로 옮길 것과 개선할 것을 정하세요.",
      readColumn: "레거시에서 읽어낸 것",
      convertColumn: "전환하면",
      fieldsLine: "입력 필드 {count}개",
      tablesLine: "테이블 {count}개",
      actionsLine: "버튼·액션 {count}개",
      scriptletsLine: "서버 스크립틀릿 {count}곳",
      inlineHandlersLine: "인라인 이벤트 {count}곳",
      convertFieldsLine: "입력 컴포넌트 {count}개",
      convertTablesLine: "데이터 테이블 (컬럼 유지)",
      convertActionsLine: "이벤트·ActionFlow {count}개",
      convertScriptletsLine: "화면과 함께 오지 않습니다 — 데이터 출처가 필요합니다",
      convertInlineHandlersLine: "이벤트 바인딩으로 자동 전환",
      decisionsTitle: "차이 목록 — 그대로 옮길까요, 개선할까요?",
      keepLabel: "그대로",
      improveLabel: "개선",
      traits: {
        tableLayout: "테이블 레이아웃",
        alertConfirm: "alert/confirm 알림",
        popupWindow: "팝업 창 (window.open)",
        deprecatedMarkup: "구식 마크업 (font/center/bgcolor)",
      },
      summary: "그대로 {keep} · 개선 {improve}",
      dataWarning: "서버 로직 {count}곳은 화면과 함께 오지 않습니다 — 데이터가 어디서 오는지는 계약이나 전문 필드 표로 알려주세요.",
      confirm: "이 결정으로 전환 진행",
      dismiss: "아직입니다 — 수정할게요",
    },
    screenDesignReadback: {
      title: "화면 설계서에서 읽어낸 것",
      countsLine: "영역 {areas}개 · 라벨 {labels}개 · 흐름 {flows}개",
      areasTitle: "영역 구성",
      labelsTitle: "라벨 — 따옴표 인용은 그대로 사용됩니다",
      flowsTitle: "화면 흐름",
      unreadHeadings:
        "문서의 절 제목으로 읽었지만 화면 영역으로는 세지 않았습니다 — 이 제목 아래 내용은 이 카드에 담기지 않았습니다:",
      dataCandidates:
        "필드 코드처럼 보이지만, 무엇에 연결되는지 문서가 말하지 않아 데이터 원천으로 세지 않았습니다 — 여기에 바인딩하지 않습니다:",
      dataGap:
        "지금 화면은 있는데 데이터가 없습니다 — 이 설계서에서 필드-거래 연결을 읽어내지 못했습니다. 계약이나 전문 필드 표를 붙이면 데이터까지 연결됩니다.",
      dataDeclared:
        "데이터 원천이 이 설계서 안에 있습니다 — 문서에서 읽어낸 참조이며, 화면과 함께 전달됩니다:",
      proceed: "화면만 우선 진행",
      proceedWithData: "이 내용으로 진행",
      dismiss: "아직입니다 — 수정할게요",
    },
    referenceExtractionReadback: {
      title: "표준 참조에서 추출",
      intentLabel: "만들 것:",
      hint: "참조를 복제하지 않습니다 — 표준만 가져갑니다. 칩을 누르면 표준 용어로 표시되고, 표시하지 않은 용어는 이 화면 고유로 남습니다.",
    dictionaryHit: '표준 용어사전에 정의되어 있습니다 — 표준 후보',
    dictionaryMiss: '용어사전에 아직 정의되지 않았습니다 — 이 화면 전용일 수 있습니다',
      structureLabel: "구조 패턴 가져가기",
      structureLegacyLine: "입력 {fields}개 · 테이블 {tables}개 · 액션 {actions}개",
      structureDesignLine: "영역 {areas}개 · 흐름 {flows}개",
      termsTitle: "용어 — 표준입니까, 이 화면 고유입니까?",
      allLabel: "전체 표준",
      identifiersNote: "필드 식별자 {count}개는 가져가지 않습니다 — 새 화면의 필드는 그 화면의 계약이나 전문 표에서 옵니다.",
      summary: "표준 {standard}개 · 이 화면 고유 {specific}개",
      confirm: "표준만 추출해 진행",
      dismiss: "아직입니다 — 수정할게요",
    },
  },
  ja: {
    studio: "studio",
    showroom: "Showroom",
    account: "アカウント",
    firstTitle: "何を作りたいですか？",
    firstSubtitle:
      "言葉で説明しても、ファイルをつないでもかまいません。残りは一つの会話で続きます。",
    firstPlaceholder: "必要なプロダクト、業務、文書、変更内容を説明してください",
    returningPlaceholder:
      "何でも話してください — 新しく作ることも、今のプロダクトを変えることもできます",
    returningPrefix: "おかえりなさい —",
    returningSuffix: "の続きから始められます。",
    sourceAction: "ファイル · Figma、Word、HWPX",
    sourceActionNoFigma: "ファイル · Word、HWPX",
    conversationHint:
      "プロダクト名を伝えると、その文脈で会話が続きます。必要な場合だけ最大3問を確認します。",
    contextTitle: "作業コンテキストを選択",
    contextHint:
      "ボットはプロジェクトなしで独立して動作します。プロジェクトチャットはアプリと編集画面のコンテキストを開いてから会話を始めます。",
    botContextTitle: "ボットに依頼",
    botContextDetail: "プロジェクトや編集画面を開かずに、何でも依頼できます。",
    botContextAction: "ボットを開く",
    projectChatContextTitle: "プロジェクトチャットで続ける",
    projectChatContextDetail: "{project} の画面、データ、編集画面のコンテキストを開いて会話します。",
    projectChatContextAction: "プロジェクトチャットを開く",
    projectChatContextUnavailable: "プロジェクトチャットはプロジェクトを作成または開いた後に利用できます。",
    advancedStartHint:
      "新しい依頼から始めるか、ソースファイルを取り込めます。",
    openProject: "開いて作業",
    reviewProject: "確認する",
    viewAllProjects: "マイプロジェクト",
    showWorkingProducts: "動いている完成品を見る",
    showroomHint:
      "完成品の選択は任意です。選択してもプレビュー候補のみで、自動公開・デプロイはしません。",
    carriedRequest: "coogoから会話を継続",
    carriedRequestDetail:
      "先ほどの依頼をこの会話に引き継ぎました。作成前に確認できます。",
    clarificationLabel: "続行に必要な質問",
    recentWork: "最近の作業",
    needsAttention: "確認が必要な作業",
    startAnotherWay: "始め方を選ぶ",
    noWorkYet: "まだ作業はありません",
    starterDoorLead:
      "まず完成したアプリを開いて動きを確かめてください。そのまま直しても、新しく始めても構いません。",
    starterDoorAction: "ニュースレター会員アプリを開く",
    starterDoorProjectName: "ニュースレター会員",
    starterDoorFailed: "スターターアプリを開けませんでした",
    newProject: "新しく作る",
    sources: "ファイル・Figmaから始める",
    starters: "スターターから始める",
    startModeGroupLabel: "どのように始めますか?",
    startModeNew: "新しく作る",
    startModeConversion: "既存画面の変換",
    conversionAsk: "元の画面を貼り付けてください。変更する前に読み取ります。",
    conversionWithoutOriginal:
      "元画面がないため新規構築として進めます — 変換ではありません。",
    projectStates: {
      draft: "下書き",
      active: "作業中",
      published: "公開済み",
      archived: "アーカイブ",
    },
    proofStates: {
      verified: "本番環境で確認済み",
      review: "根拠の確認が必要",
      blocked: "次の手順はブロック中",
      none: "本番環境の確認前",
    },
      contractReadback: {
      title: "ドキュメントから読み取った内容",
      endpointCount: "エンドポイント {count} 件",
      fieldCount: "宣言済みフィールド {count} 個",
      unfilledWarning:
        "宣言のみでサーバーが値を入れないレスポンスフィールドが {count} 個 — 画面では常に空欄になります:",
      ambiguousNote:
        "ドキュメントが決めていない箇所が {count} 箇所 — 作成前に質問で確認します。",
      unreadTypes:
        "読み取れなかった宣言が {count} 件あります — フィールドがこのドキュメントの外で定義・合成されている型か、自分のフィールドもこのドキュメントで定義された型も指していないリクエスト/レスポンスのブロックです。上の件数には含まれていません:",
      confirm: "合っています — このまま進む",
      dismiss: "まだです — 編集します",
    },
    fieldSpecReadback: {
      title: "電文フィールド表から読み取った内容",
      fieldCount: "フィールド {count} 個",
      hint: "画面に表示するフィールドだけをチェックしてください — 電文フィールドを画面へ丸ごと並べることはしません。",
      sections: {
        input: "入力部（照会条件）",
        output: "出力部（結果）",
        common: "共通部",
        unknown: "区分なし",
      },
      sectionAllLabel: "すべて",
      requiredMark: "必須",
      optionalMark: "任意",
      unreadColumns:
        "表の列 {count} 個は読み取れませんでした。このカードには反映されていません:",
      unreadRows:
        "フィールド名の欄が空で読み取れなかった行が {count} 行あります — Excel の結合セルを貼り付けるとこうなります。上の件数は読み取れた分であり、表の大きさではありません。",
      unreadRequiredValues:
        "表に必須列はありますが、その中の次の値を読み取れませんでした — この表のフィールドに必須マークが付かないのは、表が空だからではなくこのためです:",
      summary: "画面表示 {visible} 個 · 内部連携 {internal} 個",
      confirm: "この決定で進む",
      dismiss: "まだです — 編集します",
    },
    legacyScreenReadback: {
      title: "レガシー画面から読み取った内容",
      hint: "新規作成ではなく移行です — そのまま移すものと改善するものを決めてください。",
      readColumn: "レガシーから読み取ったもの",
      convertColumn: "移行すると",
      fieldsLine: "入力フィールド {count} 個",
      tablesLine: "テーブル {count} 個",
      actionsLine: "ボタン・アクション {count} 個",
      scriptletsLine: "サーバースクリプトレット {count} 箇所",
      inlineHandlersLine: "インラインイベント {count} 箇所",
      convertFieldsLine: "入力コンポーネント {count} 個",
      convertTablesLine: "データテーブル（列を維持）",
      convertActionsLine: "イベント・ActionFlow {count} 個",
      convertScriptletsLine: "画面と一緒には移りません — データの出所が必要です",
      convertInlineHandlersLine: "イベントバインディングへ自動移行",
      decisionsTitle: "差分リスト — そのまま移しますか、改善しますか？",
      keepLabel: "そのまま",
      improveLabel: "改善",
      traits: {
        tableLayout: "テーブルレイアウト",
        alertConfirm: "alert/confirm ダイアログ",
        popupWindow: "ポップアップ (window.open)",
        deprecatedMarkup: "旧式マークアップ (font/center/bgcolor)",
      },
      summary: "そのまま {keep} · 改善 {improve}",
      dataWarning: "サーバーロジック {count} 箇所は画面と一緒には移りません — データの出所は契約か電文フィールド表で伝えてください。",
      confirm: "この決定で移行する",
      dismiss: "まだです — 編集します",
    },
    screenDesignReadback: {
      title: "画面設計書から読み取った内容",
      countsLine: "領域 {areas} 個 · ラベル {labels} 個 · フロー {flows} 個",
      areasTitle: "領域構成",
      labelsTitle: "ラベル — 引用された文言はそのまま使われます",
      flowsTitle: "画面フロー",
      unreadHeadings:
        "ドキュメントの節見出しとして読みましたが、画面領域としては数えていません — この見出しの下の内容はこのカードに載っていません:",
      dataCandidates:
        "フィールドコードのように見えますが、何に結び付くのかドキュメントが述べていないため、データの出所としては数えていません — ここには何もバインドしません:",
      dataGap:
        "画面はありますが、データがありません — この設計書からフィールドと取引の対応を読み取れませんでした。契約か電文フィールド表を添付すればデータまでつながります。",
      dataDeclared:
        "データの出所はこの設計書の中にあります — ドキュメントから読み取った参照で、画面と一緒に渡されます:",
      proceed: "画面のみ先行して進む",
      proceedWithData: "この内容で進む",
      dismiss: "まだです — 編集します",
    },
    referenceExtractionReadback: {
      title: "参照から標準を抽出",
      intentLabel: "作るもの:",
      hint: "参照は複製しません — 標準だけを持っていきます。チップを押すと標準用語になり、押さなかった用語はこの画面固有のままです。",
    dictionaryHit: 'Defined in the standard terminology index — a standard candidate',
    dictionaryMiss: 'Not yet defined in the terminology index — may be screen-specific',
      structureLabel: "構造パターンを持っていく",
      structureLegacyLine: "入力 {fields} 個 · テーブル {tables} 個 · アクション {actions} 個",
      structureDesignLine: "領域 {areas} 個 · フロー {flows} 個",
      termsTitle: "用語 — 標準ですか、この画面固有ですか？",
      allLabel: "すべて標準",
      identifiersNote: "フィールド識別子 {count} 個は持っていきません — 新しい画面のフィールドはその画面の契約や電文表から来ます。",
      summary: "標準 {standard} 個 · この画面固有 {specific} 個",
      confirm: "標準だけ抽出して進む",
      dismiss: "まだです — 編集します",
    },
  },
  zh: {
    studio: "studio",
    showroom: "Showroom",
    account: "账户",
    firstTitle: "你想创建什么？",
    firstSubtitle:
      "可以直接描述，也可以连接文件，其余步骤会在同一段对话中继续。",
    firstPlaceholder: "描述所需的产品、流程、文档或更改",
    returningPlaceholder: "告诉 coogo 任何需求 — 新建内容或修改现有产品",
    returningPrefix: "欢迎回来 —",
    returningSuffix: "可以从上次的位置继续。",
    sourceAction: "文件 · Figma、Word、HWPX",
    sourceActionNoFigma: "文件 · Word、HWPX",
    conversationHint:
      "说出产品名称即可在对应上下文中继续。仅在必要时最多询问三个问题。",
    contextTitle: "选择工作上下文",
    contextHint:
      "机器人可在没有项目的情况下独立工作。项目对话会先打开应用及其编辑器上下文，再开始交流。",
    botContextTitle: "向机器人提出请求",
    botContextDetail: "无需打开项目或编辑器，即可提出任何请求。",
    botContextAction: "打开机器人",
    projectChatContextTitle: "在项目对话中继续",
    projectChatContextDetail: "打开 {project} 的页面、数据和编辑器上下文后继续交流。",
    projectChatContextAction: "打开项目对话",
    projectChatContextUnavailable: "请先创建或打开项目，再使用项目对话。",
    advancedStartHint:
      "可从新需求开始，或在这里导入源文件。",
    openProject: "打开并继续",
    reviewProject: "检查",
    viewAllProjects: "我的项目",
    showWorkingProducts: "浏览可运行的成品",
    showroomHint:
      "选择成品是可选的。选择后也只创建预览候选，不会自动发布或部署。",
    carriedRequest: "继续 coogo 中的对话",
    carriedRequestDetail: "先前的请求已带入这段对话，创建前可以先检查。",
    clarificationLabel: "继续所需的问题",
    recentWork: "最近的工作",
    needsAttention: "需要处理",
    startAnotherWay: "选择开始方式",
    noWorkYet: "还没有工作",
    starterDoorLead:
      "先打开一个完成的应用，看看它如何运行。之后可以直接修改，也可以另起一个。",
    starterDoorAction: "打开订阅会员应用",
    starterDoorProjectName: "订阅会员",
    starterDoorFailed: "无法打开起步应用",
    newProject: "从新需求开始",
    sources: "从文件或 Figma 开始",
    starters: "从模板开始",
    startModeGroupLabel: "想怎么开始?",
    startModeNew: "新建",
    startModeConversion: "转换现有页面",
    conversionAsk: "请粘贴原始页面，我会先读取再进行修改。",
    conversionWithoutOriginal: "未提供原始页面，将按新建方式进行 — 这不是转换。",
    projectStates: {
      draft: "草稿",
      active: "进行中",
      published: "已发布",
      archived: "已归档",
    },
    proofStates: {
      verified: "已有生产回读记录",
      review: "证据需要检查",
      blocked: "下一步已受阻",
      none: "尚无生产回读",
    },
      contractReadback: {
      title: "从文档中读取的内容",
      endpointCount: "{count} 个接口",
      fieldCount: "{count} 个已声明字段",
      unfilledWarning:
        "有 {count} 个响应字段只声明而服务器从不填充 — 页面上将始终为空:",
      ambiguousNote:
        "文档未决定的地方有 {count} 处 — 创建前会以问题形式确认。",
      unreadTypes:
        "有 {count} 处声明未被读取 — 可能是字段在这份文档之外定义或组合的类型，也可能是既没有自己的字段、也没有指向本文档所定义类型的请求/响应块。它们没有计入上面的数量:",
      confirm: "正确 — 继续",
      dismiss: "还没好 — 我再改",
    },
    fieldSpecReadback: {
      title: "从报文字段表中读取的内容",
      fieldCount: "{count} 个字段",
      hint: "只勾选需要显示在页面上的字段 — 报文字段不会整体铺到页面上。",
      sections: {
        input: "输入部（查询条件）",
        output: "输出部（结果）",
        common: "公共部",
        unknown: "未分区",
      },
      sectionAllLabel: "全部",
      requiredMark: "必填",
      optionalMark: "可选",
      unreadColumns:
        "表格中有 {count} 列未被读取，这张卡片没有反映它们:",
      unreadRows:
        "有 {count} 行的字段名单元格为空而未被读取 — Excel 合并单元格粘贴后就是这样。上面的数量是读到的字段数，不是表格的大小。",
      unreadRequiredValues:
        "表格里有必填列，但其中以下取值无法读懂 — 此表中的字段没有必填标记，是因为这个，而不是因为表格没写:",
      summary: "页面显示 {visible} 个 · 内部传递 {internal} 个",
      confirm: "按此决定继续",
      dismiss: "还没好 — 我再改",
    },
    legacyScreenReadback: {
      title: "从旧系统页面中读取的内容",
      hint: "这是迁移，不是新建 — 请决定哪些原样保留、哪些改进。",
      readColumn: "从旧页面读取",
      convertColumn: "迁移后",
      fieldsLine: "{count} 个输入字段",
      tablesLine: "{count} 个表格",
      actionsLine: "{count} 个按钮/操作",
      scriptletsLine: "{count} 处服务端脚本",
      inlineHandlersLine: "{count} 处内联事件",
      convertFieldsLine: "{count} 个输入组件",
      convertTablesLine: "数据表格（保留列）",
      convertActionsLine: "{count} 个事件/ActionFlow",
      convertScriptletsLine: "不会随页面迁移 — 需要数据来源",
      convertInlineHandlersLine: "自动转换为事件绑定",
      decisionsTitle: "差异清单 — 原样保留还是改进？",
      keepLabel: "原样",
      improveLabel: "改进",
      traits: {
        tableLayout: "表格布局",
        alertConfirm: "alert/confirm 弹窗",
        popupWindow: "弹出窗口 (window.open)",
        deprecatedMarkup: "过时标记 (font/center/bgcolor)",
      },
      summary: "原样 {keep} · 改进 {improve}",
      dataWarning: "{count} 处服务端逻辑不会随页面迁移 — 请用接口契约或报文字段表说明数据来源。",
      confirm: "按此决定迁移",
      dismiss: "还没好 — 我再改",
    },
    screenDesignReadback: {
      title: "从页面设计文档中读取的内容",
      countsLine: "{areas} 个区域 · {labels} 个标签 · {flows} 条流程",
      areasTitle: "区域构成",
      labelsTitle: "标签 — 引号中的文案将原样使用",
      flowsTitle: "页面流程",
      unreadHeadings:
        "已读作文档的章节标题，但没有计为页面区域 — 该标题下的内容未出现在此卡片上:",
      dataCandidates:
        "它们看起来像字段代码，但文档没有说明它们绑定到什么，因此没有计为数据来源 — 不会绑定到它们:",
      dataGap:
        "页面有了，数据还没有 — 无法从此文档读出字段与接口的对应。附上接口契约或报文字段表，数据也会连接起来。",
      dataDeclared:
        "数据来源就在这份文档里 — 以下是从中读到的引用，它们会随页面一起传递:",
      proceed: "先只做页面",
      proceedWithData: "按此内容继续",
      dismiss: "还没好 — 我再改",
    },
    referenceExtractionReadback: {
      title: "从参考中提取标准",
      intentLabel: "要做的:",
      hint: "不复制参考 — 只提取标准。点击词条将其标为标准用语，未标记的词条保留为该页面特有。",
    dictionaryHit: 'Defined in the standard terminology index — a standard candidate',
    dictionaryMiss: 'Not yet defined in the terminology index — may be screen-specific',
      structureLabel: "沿用结构模式",
      structureLegacyLine: "{fields} 个输入 · {tables} 个表格 · {actions} 个操作",
      structureDesignLine: "{areas} 个区域 · {flows} 条流程",
      termsTitle: "用语 — 是标准，还是该页面特有？",
      allLabel: "全部标准",
      identifiersNote: "{count} 个字段标识符不会带走 — 新页面的字段来自它自己的契约或报文字段表。",
      summary: "标准 {standard} 个 · 页面特有 {specific} 个",
      confirm: "仅提取标准并继续",
      dismiss: "还没好 — 我再改",
    },
  },
  "zh-TW": {
    studio: "studio",
    showroom: "Showroom",
    account: "帳戶",
    firstTitle: "你想建立什麼？",
    firstSubtitle:
      "可以直接描述，也可以連接檔案，其餘步驟會在同一段對話中繼續。",
    firstPlaceholder: "描述所需的產品、流程、文件或變更",
    returningPlaceholder: "告訴 coogo 任何需求 — 建立新內容或修改現有產品",
    returningPrefix: "歡迎回來 —",
    returningSuffix: "可以從上次的位置繼續。",
    sourceAction: "檔案 · Figma、Word、HWPX",
    sourceActionNoFigma: "檔案 · Word、HWPX",
    conversationHint:
      "說出產品名稱即可在對應脈絡中繼續。只有必要時才會詢問，最多三題。",
    contextTitle: "選擇工作情境",
    contextHint:
      "機器人可在沒有專案的情況下獨立工作。專案對話會先開啟應用及其編輯器情境，再開始交流。",
    botContextTitle: "向機器人提出請求",
    botContextDetail: "無需開啟專案或編輯器，即可提出任何請求。",
    botContextAction: "開啟機器人",
    projectChatContextTitle: "在專案對話中繼續",
    projectChatContextDetail: "開啟 {project} 的頁面、資料和編輯器情境後繼續交流。",
    projectChatContextAction: "開啟專案對話",
    projectChatContextUnavailable: "請先建立或開啟專案，再使用專案對話。",
    advancedStartHint:
      "可從新需求開始，或在這裡匯入來源檔案。",
    openProject: "開啟並繼續",
    reviewProject: "檢查",
    viewAllProjects: "我的專案",
    showWorkingProducts: "瀏覽可運作的成品",
    showroomHint:
      "選擇成品是選擇性的。選擇後也只建立預覽候選，不會自動發佈或部署。",
    carriedRequest: "繼續 coogo 中的對話",
    carriedRequestDetail: "先前的要求已帶入這段對話，建立前可以先檢查。",
    clarificationLabel: "繼續所需的問題",
    recentWork: "最近的工作",
    needsAttention: "需要處理",
    startAnotherWay: "選擇開始方式",
    noWorkYet: "還沒有工作",
    starterDoorLead:
      "先開啟一個完成的應用，看看它如何運作。之後可以直接修改，也可以另起一個。",
    starterDoorAction: "開啟訂閱會員應用",
    starterDoorProjectName: "訂閱會員",
    starterDoorFailed: "無法開啟起步應用",
    newProject: "從新需求開始",
    sources: "從檔案或 Figma 開始",
    starters: "從範本開始",
    startModeGroupLabel: "想怎麼開始?",
    startModeNew: "新建",
    startModeConversion: "轉換現有頁面",
    conversionAsk: "請貼上原始頁面，我會先讀取再進行修改。",
    conversionWithoutOriginal: "未提供原始頁面，將按新建方式進行 — 這不是轉換。",
    projectStates: {
      draft: "草稿",
      active: "進行中",
      published: "已發佈",
      archived: "已封存",
    },
    proofStates: {
      verified: "已有正式環境回讀記錄",
      review: "證據需要檢查",
      blocked: "下一步已受阻",
      none: "尚無正式環境回讀",
    },
      contractReadback: {
      title: "從文件中讀取的內容",
      endpointCount: "{count} 個介面",
      fieldCount: "{count} 個已宣告欄位",
      unfilledWarning:
        "有 {count} 個回應欄位只宣告而伺服器從不填入 — 畫面上將永遠為空:",
      ambiguousNote:
        "文件未決定的地方有 {count} 處 — 建立前會以問題形式確認。",
      unreadTypes:
        "有 {count} 處宣告未被讀取 — 可能是欄位在這份文件之外定義或組合的型別，也可能是既沒有自己的欄位、也沒有指向本文件所定義型別的請求/回應區塊。它們沒有計入上面的數量:",
      confirm: "正確 — 繼續",
      dismiss: "還沒好 — 我再改",
    },
    fieldSpecReadback: {
      title: "從電文欄位表中讀取的內容",
      fieldCount: "{count} 個欄位",
      hint: "只勾選需要顯示在畫面上的欄位 — 電文欄位不會整批鋪到畫面上。",
      sections: {
        input: "輸入部（查詢條件）",
        output: "輸出部（結果）",
        common: "共通部",
        unknown: "未分區",
      },
      sectionAllLabel: "全部",
      requiredMark: "必填",
      optionalMark: "選填",
      unreadColumns:
        "表格中有 {count} 欄未被讀取，這張卡片沒有反映它們:",
      unreadRows:
        "有 {count} 列的欄位名稱儲存格為空而未被讀取 — Excel 合併儲存格貼上後就是這樣。上面的數量是讀到的欄位數，不是表格的大小。",
      unreadRequiredValues:
        "表格裡有必填欄，但其中以下取值無法讀懂 — 此表中的欄位沒有必填標記，是因為這個，而不是因為表格沒寫:",
      summary: "畫面顯示 {visible} 個 · 內部傳遞 {internal} 個",
      confirm: "依此決定繼續",
      dismiss: "還沒好 — 我再改",
    },
    legacyScreenReadback: {
      title: "從舊系統畫面中讀取的內容",
      hint: "這是移轉，不是新建 — 請決定哪些原樣保留、哪些改進。",
      readColumn: "從舊畫面讀取",
      convertColumn: "移轉後",
      fieldsLine: "{count} 個輸入欄位",
      tablesLine: "{count} 個表格",
      actionsLine: "{count} 個按鈕/操作",
      scriptletsLine: "{count} 處伺服端腳本",
      inlineHandlersLine: "{count} 處行內事件",
      convertFieldsLine: "{count} 個輸入元件",
      convertTablesLine: "資料表格（保留欄位）",
      convertActionsLine: "{count} 個事件/ActionFlow",
      convertScriptletsLine: "不會隨畫面移轉 — 需要資料來源",
      convertInlineHandlersLine: "自動轉換為事件繫結",
      decisionsTitle: "差異清單 — 原樣保留還是改進？",
      keepLabel: "原樣",
      improveLabel: "改進",
      traits: {
        tableLayout: "表格版面",
        alertConfirm: "alert/confirm 對話框",
        popupWindow: "彈出視窗 (window.open)",
        deprecatedMarkup: "過時標記 (font/center/bgcolor)",
      },
      summary: "原樣 {keep} · 改進 {improve}",
      dataWarning: "{count} 處伺服端邏輯不會隨畫面移轉 — 請用介面契約或電文欄位表說明資料來源。",
      confirm: "依此決定移轉",
      dismiss: "還沒好 — 我再改",
    },
    screenDesignReadback: {
      title: "從畫面設計文件中讀取的內容",
      countsLine: "{areas} 個區域 · {labels} 個標籤 · {flows} 條流程",
      areasTitle: "區域構成",
      labelsTitle: "標籤 — 引號中的文案將原樣使用",
      flowsTitle: "畫面流程",
      unreadHeadings:
        "已讀作文件的章節標題，但沒有計為畫面區域 — 該標題下的內容未出現在此卡片上:",
      dataCandidates:
        "它們看起來像欄位代碼，但文件沒有說明它們綁定到什麼，因此沒有計為資料來源 — 不會綁定到它們:",
      dataGap:
        "畫面有了，資料還沒有 — 無法從此文件讀出欄位與介面的對應。附上介面契約或電文欄位表，資料也會連接起來。",
      dataDeclared:
        "資料來源就在這份文件裡 — 以下是從中讀到的參照，它們會隨畫面一起傳遞:",
      proceed: "先只做畫面",
      proceedWithData: "依此內容繼續",
      dismiss: "還沒好 — 我再改",
    },
    referenceExtractionReadback: {
      title: "從參考中擷取標準",
      intentLabel: "要做的:",
      hint: "不複製參考 — 只擷取標準。點擊詞條將其標為標準用語，未標記的詞條保留為該畫面特有。",
    dictionaryHit: 'Defined in the standard terminology index — a standard candidate',
    dictionaryMiss: 'Not yet defined in the terminology index — may be screen-specific',
      structureLabel: "沿用結構模式",
      structureLegacyLine: "{fields} 個輸入 · {tables} 個表格 · {actions} 個操作",
      structureDesignLine: "{areas} 個區域 · {flows} 條流程",
      termsTitle: "用語 — 是標準，還是該畫面特有？",
      allLabel: "全部標準",
      identifiersNote: "{count} 個欄位識別碼不會帶走 — 新畫面的欄位來自它自己的契約或電文欄位表。",
      summary: "標準 {standard} 個 · 畫面特有 {specific} 個",
      confirm: "僅擷取標準並繼續",
      dismiss: "還沒好 — 我再改",
    },
  },
};

export function getCoogoStudioHomeRenewalCopy(
  locale: LocaleCode,
): CoogoStudioHomeRenewalCopy {
  return COPY[locale];
}
